import { useState } from 'react'
import { TableShell } from '../../components/molecules/TableShell'
import { Alert } from '../../components/atoms/Alert'
import { Button } from '../../components/atoms/Button'
import { Spinner } from '../../components/atoms/Spinner'
import { Th, Td } from '../../components/molecules/Table'
import { Paginator } from '../../components/molecules/Paginator'
import { CategoryFormModal } from './CategoryFormModal'
import { useCategories, type CategoryDto } from '../../../viewmodels/useCategories'
import { useAuth } from '../../../context/AuthContext'
import { SCREENCODES } from '../../../constants/screen.constants'

/**
 * /manage/categories — list + create/edit/delete.
 *
 * Mirrors src/ui/pages/manage/AttributesPage.tsx with two changes:
 *   - Attributes-count column (derived from category_attributes JOIN
 *     in the list query — see CategoryRepository.findPaginatedWithAttributes).
 *   - Form modal opens a multi-select AttributePicker (mirrors the
 *     GroupsPicker pattern from UserTypeFormModal).
 */
export function CategoriesPage(): JSX.Element {
  const {
    items,
    attributes,
    total,
    page,
    pageSize,
    isLoading,
    error,
    setPage,
    reload,
    create,
    update,
    remove
  } = useCategories()
  const { can } = useAuth()
  const [modalMode, setModalMode] = useState<'closed' | 'create' | 'edit'>('closed')
  const [editTarget, setEditTarget] = useState<CategoryDto | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const openCreate = (): void => {
    setEditTarget(null)
    setModalMode('create')
  }
  const openEdit = (row: CategoryDto): void => {
    setEditTarget(row)
    setModalMode('edit')
  }
  const closeModal = (): void => setModalMode('closed')

  const handleDelete = async (row: CategoryDto): Promise<void> => {
    if (typeof window !== 'undefined' && !window.confirm('Are you sure you want to delete?')) return
    setDeletingId(row.id)
    setDeleteError(null)
    const res = await remove(row.id)
    setDeletingId(null)
    if (!res.ok) setDeleteError(res.message)
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between gap-4">
        <h1 className="font-heading text-h2 text-charcoal">Categories</h1>
        {can(SCREENCODES.MANAGECATEGORIES, 'add') ? <Button onClick={openCreate}>Add</Button> : null}
      </header>

      {deleteError !== null ? <Alert tone="error">{deleteError}</Alert> : null}
      {error !== null ? (
        <Alert tone="error">
          {error}{' '}
          <button
            type="button"
            className="underline underline-offset-2 hover:text-charcoal"
            onClick={() => void reload()}
          >
            Retry
          </button>
        </Alert>
      ) : null}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner />
        </div>
      ) : items.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          <Table
            items={items}
            onEdit={openEdit}
            onDelete={(row) => void handleDelete(row)}
            deletingId={deletingId}
          />
          <Paginator
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
            disabled={isLoading}
          />
        </>
      )}

      <CategoryFormModal
        open={modalMode !== 'closed'}
        mode={modalMode === 'edit' ? 'edit' : 'create'}
        initial={editTarget}
        attributes={attributes}
        onClose={closeModal}
        onSubmit={async (input) => {
          if (modalMode === 'edit' && editTarget !== null) {
            return update(editTarget.id, input)
          }
          return create(input as Parameters<typeof create>[0])
        }}
      />
    </div>
  )
}

function EmptyState(): JSX.Element {
  return (
    <div className="text-center py-16 border border-border rounded-md bg-parchment/40">
      <p className="font-heading text-h3 text-charcoal">No categories yet</p>
      <p className="font-body text-body-sm text-text-secondary mt-2">
        Use <span className="font-medium">Add</span> at the top right to create
        the first category.
      </p>
    </div>
  )
}

function Table({
  items,
  onEdit,
  onDelete,
  deletingId
}: {
  readonly items: CategoryDto[]
  readonly onEdit: (row: CategoryDto) => void
  readonly onDelete: (row: CategoryDto) => void
  readonly deletingId: string | null
}): JSX.Element {
  const { can } = useAuth()
  const canEdit = can(SCREENCODES.MANAGECATEGORIES, 'edit')
  const canDelete = can(SCREENCODES.MANAGECATEGORIES, 'delete')
  return (
    <TableShell>
      <table className="w-full font-body text-body-sm">
        <thead className="bg-parchment/50 text-text-secondary">
          <tr>
            <Th>Name</Th>
            <Th>Description</Th>
            <Th>Attributes</Th>
            <Th>Products</Th>
            <Th className="text-right">Actions</Th>
          </tr>
        </thead>
        <tbody>
          {items.map((row) => {
            const locked = row.linked_product_count > 0
            const lockTooltip = locked
              ? `Locked: ${row.linked_product_count} active product${row.linked_product_count === 1 ? '' : 's'} belong${row.linked_product_count === 1 ? 's' : ''} to this category. Rename + description are still allowed.`
              : ''
            return (
              <tr key={row.id} className="border-t border-border">
                <Td>{row.name}</Td>
                <Td
                  className="max-w-[24rem] truncate"
                  title={row.description === null ? '' : row.description}
                >
                  {row.description ?? <span className="text-text-secondary">—</span>}
                </Td>
                <Td>{row.attribute_count}</Td>
                <Td>
                  {locked ? (
                    <span
                      className="inline-flex items-center px-2 py-0.5 rounded-full bg-bamboo/20 text-charcoal font-medium text-body-xs"
                      title={lockTooltip}
                    >
                      {row.linked_product_count} product{row.linked_product_count === 1 ? '' : 's'}
                    </span>
                  ) : (
                    <span className="text-text-secondary">—</span>
                  )}
                </Td>
                <Td className="text-right whitespace-nowrap">
                  {canEdit ? (
                    <>
                      <Button size="sm" variant="secondary" onClick={() => onEdit(row)}>
                        Edit
                      </Button>{' '}
                    </>
                  ) : null}
                  {canDelete ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onDelete(row)}
                      disabled={deletingId === row.id || locked}
                      title={locked ? lockTooltip : ''}
                    >
                      {deletingId === row.id ? 'Deleting…' : 'Delete'}
                    </Button>
                  ) : null}
                </Td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </TableShell>
  )
}
